import { classNames } from "@/react/helpers/twMergeWithCN";
import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { CommunityMember } from "@circle-react/types";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";
import { UserImage } from "../../../uikit/UserImage";
import { ConnectionButtons } from "../ActionButtons/ConnectionButtons";
import { MemberGradientBackground } from "./MemberGradientBackground";

export interface MemberCardWithConnectionProps {
  member: CommunityMember;
}

export const MemberCardWithConnection = ({
  member,
}: MemberCardWithConnectionProps) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: member.public_uid ?? "",
  });

  return (
    <li
      className={classNames(
        "bg-primary outline-primary hover:shadow-c-card relative inline-flex h-[274px] w-[244px] flex-col items-center justify-between overflow-hidden rounded-2xl px-3 pb-4 pt-3 outline outline-1 outline-offset-[-1px]",
      )}
      data-testid="community_member_grid"
    >
      <MemberGradientBackground member={member} />
      <a
        {...profileLinkProps}
        className="flex w-full flex-col items-center justify-start gap-4 rounded-lg py-4"
        title={member.name}
      >
        <div className="size-16">
          <UserImage
            name={member.name ?? ""}
            src={member.avatar_url ?? ""}
            size="16"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-2.5">
          <div className="flex w-full flex-col items-center justify-start gap-0.5">
            <span className="w-full text-center">
              <Typography.BodySm
                truncate
                color="text-darkest"
                as="h2"
                weight="font-semibold"
              >
                {member.name}
              </Typography.BodySm>
            </span>

            <span className="w-full text-center">
              <Typography.BodyXs truncate color="text-default">
                {member.headline}
              </Typography.BodyXs>
            </span>
          </div>
          {member.location && (
            <div className="flex w-full items-center justify-center gap-1 px-2">
              <Icon
                type="16-location"
                size={16}
                className="text-default shrink-0"
              />
              <span className="min-w-0 text-center">
                <Typography.BodyXs
                  truncate
                  color="text-default"
                  weight="font-medium"
                >
                  {member.location}
                </Typography.BodyXs>
              </span>
            </div>
          )}
        </div>
      </a>
      <div className="inline-flex w-full items-start justify-start gap-2 px-1">
        <ConnectionButtons member={member} className="w-full" />
      </div>
    </li>
  );
};
